import { isDefiniteShopRejection } from "./shopMutationOutcome";
import { useEffect, useRef, useState } from "react";
import { ShopApiError } from "./shopAdminApi";

// One mounted editor owns an immutable submission until the outcome is known.
// Callers copy their body before building the closure. No Firestore fallback.
export function useConfirmedShopMutation(onSaved: () => void, actorUid: string | undefined, onDenied?: () => void) {
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const frozen = useRef<{ run: (key: string) => Promise<unknown>; key: string; actorUid: string; uncertain?: boolean } | null>(null);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const reset = () => { if (inFlight.current || frozen.current) return; setConflict(false); setError(""); };
  const submit = async (run: (key: string) => Promise<unknown>) => {
    if (!actorUid || inFlight.current || conflict) return;
    if (!frozen.current) frozen.current = { run, key: crypto.randomUUID(), actorUid };
    const attempt = frozen.current;
    if (attempt.actorUid !== actorUid) { setError("Logga in som användaren som påbörjade sparandet för att bekräfta samma åtgärd."); return; }
    inFlight.current = true; setSaving(true); setPending(true); setError("");
    let confirmed = false;
    try {
      await attempt.run(attempt.key);
      if (!mounted.current) return;
      frozen.current = null; setPending(false); confirmed = true;
    } catch (caught) {
      if (!mounted.current) return;
      if (isDefiniteShopRejection(caught, attempt.uncertain)) {
        frozen.current = null; setPending(false); setConflict(true);
        setError(caught instanceof ShopApiError && (caught.status === 401 || caught.status === 403) ? "Du saknar aktuell behörighet att spara." :
          "Uppgifterna har ändrats eller ändringen är spärrad. Hämta aktuella uppgifter och granska igen.");
        if (caught instanceof ShopApiError && [401, 403].includes(caught.status)) onDenied?.();
      } else { attempt.uncertain = true; setError("Sparandet kunde inte bekräftas. Försök igen med samma uppgifter innan du gör en ny ändring."); }
    } finally { inFlight.current = false; if (mounted.current) setSaving(false); }
    if (confirmed) onSaved();
  };
  const retry = () => frozen.current ? submit(frozen.current.run) : Promise.resolve();
  return { saving, pending, conflict, error, reset, submit, retry };
}
